import { useFrame } from '@react-three/fiber';
import { SkyMaterial } from '@takram/three-atmosphere';
import type { AtmosphereApi } from '@takram/three-atmosphere/r3f';
import { useEffect, useMemo, type MutableRefObject } from 'react';
import {
  CubeCamera,
  HalfFloatType,
  Mesh,
  PlaneGeometry,
  Scene,
  Vector3,
  WebGLCubeRenderTarget,
} from 'three';

export const useSkyEnvironment = (
  atmosphereRef: MutableRefObject<AtmosphereApi | null>
) => {
  const environment = useMemo(() => {
    const target = new WebGLCubeRenderTarget(64, { type: HalfFloatType });
    const camera = new CubeCamera(0.1, 10, target);
    camera.position.y = 1;
    const material = new SkyMaterial({ sun: false, moon: false });
    const geometry = new PlaneGeometry(2, 2);
    const quad = new Mesh(geometry, material);
    quad.frustumCulled = false;
    const scene = new Scene();
    scene.add(quad);
    return {
      target,
      camera,
      material,
      geometry,
      scene,
      sun: new Vector3(),
      updatedAt: -Infinity,
    };
  }, []);

  useEffect(
    () => () => {
      environment.target.dispose();
      environment.material.dispose();
      environment.geometry.dispose();
    },
    [environment]
  );

  useFrame(({ gl, clock }) => {
    const api = atmosphereRef.current;
    if (!api?.textures) return;
    const elapsed = clock.elapsedTime;
    if (elapsed - environment.updatedAt < 0.1) return;
    if (environment.sun.distanceToSquared(api.sunDirection) < 1e-8) return;
    const { material } = environment;
    Object.assign(material, api.textures);
    material.sunDirection.copy(api.sunDirection);
    material.worldToECEFMatrix.copy(api.worldToECEFMatrix);
    environment.camera.update(gl, environment.scene);
    environment.sun.copy(api.sunDirection);
    environment.updatedAt = elapsed;
  });

  return environment.target.texture;
};
